/**
 * Hauptkomponente der FireCore-Anwendung
 * @module App
 */

import React, { useEffect, useState } from 'react';
import { Switch, Route, Redirect, useLocation } from 'react-router-dom';
import { focusHandling } from 'cruip-js-toolkit';
import ErrorNotification from './components/ErrorNotification';
import ActionTrace from './components/ActionTrace';

import Dashboard from './pages/Dashboard';
import Company from './pages/Settings/Company';
import User from './pages/Settings/User';
import Employees from './pages/Settings/Employee';
import Devices from './pages/Devices';
import DeviceOpen from './pages/DeviceOpen';
import DevicesStatus from './pages/DevicesStatus';
import Events from './pages/Events';
import AuthLogin from './pages/auth/Login';
import AuthLogout from './pages/auth/Logout';
import AuthVerify from './pages/auth/Verify';
import AuthSignup from './pages/auth/Signup';
import PublicAlarms from './pages/PublicAlarms';
import { getApiBaseUrl } from './utils/apiBaseUrl';

function App() {
  const location = useLocation();
  const [isAuth, setIsAuth] = useState(false);
  const [authLoaded, setAuthLoaded] = useState(false);

  useEffect(() => {
    document.querySelector('html').style.scrollBehavior = 'auto';
    window.scroll({ top: 0 });
    document.querySelector('html').style.scrollBehavior = '';
    focusHandling('outline');
    getAuth();
  }, [location.pathname]);

  const getAuth = async () => {
    try {
      const res = await fetch(`${getApiBaseUrl()}v2/authentication/status/`, {
        credentials: 'include'
      });
      const text = await res.text();
      if (!text || text.trim() === '') {
        setIsAuth(false);
        setAuthLoaded(true);
        return;
      }
      const data = JSON.parse(text);
      if (data.success === true) {
        setIsAuth(true);
      } else {
        setIsAuth(false);
      }
    } catch (err) {
      setIsAuth(false);
    } finally {
      setAuthLoaded(true);
    }
  };

  return (
    <>
      <ErrorNotification />
      <ActionTrace />
      <Switch>
        <Route exact path="/public" component={PublicAlarms} />
        <PrivateRoute exact path="/" component={Dashboard} authLoaded={authLoaded} isAuth={isAuth} />
        <PrivateRoute exact path="/devices/status" component={DevicesStatus} authLoaded={authLoaded} isAuth={isAuth} />
        <PrivateRoute exact path="/devices/:id" component={DeviceOpen} authLoaded={authLoaded} isAuth={isAuth} />
        <PrivateRoute exact path="/devices" component={Devices} authLoaded={authLoaded} isAuth={isAuth} />
        <PrivateRoute exact path="/events" component={Events} authLoaded={authLoaded} isAuth={isAuth} />
        <PrivateRoute exact path="/auth/logout" component={AuthLogout} authLoaded={authLoaded} isAuth={isAuth} />
        <PrivateRoute exact path="/company" component={Company} authLoaded={authLoaded} isAuth={isAuth} />
        <PrivateRoute exact path="/employees" component={Employees} authLoaded={authLoaded} isAuth={isAuth} />
        <PrivateRoute exact path="/user" component={User} authLoaded={authLoaded} isAuth={isAuth} />

        <PublicRoute path="/auth/login" authLoaded={authLoaded} isAuth={isAuth}>
          <AuthLogin />
        </PublicRoute>
        <PublicRoute path="/auth/register" authLoaded={authLoaded} isAuth={isAuth}>
          <AuthSignup />
        </PublicRoute>
        <PublicRoute path="/auth/verify" authLoaded={authLoaded} isAuth={isAuth}>
          <AuthVerify />
        </PublicRoute>

        <ErrorRoute path="*" authLoaded={authLoaded} isAuth={isAuth} />
      </Switch>
    </>
  );
}

function PrivateRoute({ ...rest }) {
  return (
    <>
      {rest.authLoaded === true && (
        <>
          {rest.isAuth ? (
            <Route {...rest} />
          ) : (
            <Redirect to="/auth/login" />
          )}
        </>
      )}
    </>
  );
}

function PublicRoute({ children, ...rest }) {
  return (
    <Route
      {...rest}
      render={() =>
        rest.isAuth ? (
          <Redirect to="/" />
        ) : (
          children
        )
      }
    />
  );
}

function ErrorRoute({ ...rest }) {
  return (
    <>
      {rest.authLoaded === true && (
        <Route
          {...rest}
          render={() =>
            rest.isAuth ? (
              <Redirect to="/" />
            ) : (
              <Redirect to="/auth/login" />
            )
          }
        />
      )}
    </>
  );
}

export default App;
